import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAdminAuthContext } from '../context/AdminAuthContext';
import '../components/AdminLayout.css';
import './Settings.css';

export default function Settings() {
  const { maintenanceMode, setMaintenanceMode } = useOutletContext();
  const { session, signOut } = useAdminAuthContext();
  const [maintMsg, setMaintMsg] = useState('');
  const [maintEnabled, setMaintEnabled] = useState(maintenanceMode);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [stats, setStats] = useState({ users: 0, notes: 0, matches: 0 });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  useEffect(() => { setMaintEnabled(maintenanceMode); }, [maintenanceMode]);

  async function fetchSettings() {
    const { data } = await supabase
      .from('app_settings')
      .select('maintenance_mode, maintenance_message, updated_at, updated_by')
      .eq('id', 'general')
      .single();
    if (data) {
      setMaintEnabled(data.maintenance_mode);
      setMaintMsg(data.maintenance_message || '');
      setLastUpdated(data.updated_at);
      setUpdatedBy(data.updated_by);
    }
  }

  async function fetchStats() {
    const [{ count: users }, { count: notes }, { count: matches }] = await Promise.all([
      supabase.from('profiles').select('*', { count: 'exact', head: true }),
      supabase.from('notes').select('*', { count: 'exact', head: true }),
      supabase.from('matches').select('*', { count: 'exact', head: true }),
    ]);
    setStats({ users: users || 0, notes: notes || 0, matches: matches || 0 });
  }

  async function handleSave() {
    setSaving(true);
    const { error } = await supabase.from('app_settings').upsert({
      id: 'general',
      maintenance_mode: maintEnabled,
      maintenance_message: maintMsg,
      updated_at: new Date().toISOString(),
      updated_by: session?.user?.email,
    }, { onConflict: 'id' });

    setSaving(false);
    if (!error) {
      setMaintenanceMode(maintEnabled);
      setSaved(true);
      setLastUpdated(new Date().toISOString());
      setUpdatedBy(session?.user?.email);
      setTimeout(() => setSaved(false), 3000);
    }
  }

  function fmtDate(d) { return d ? new Date(d).toLocaleString('tr-TR') : '-'; }

  return (
    <div className="settings-page">
      {/* Admin Account */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <span className="settings-section-icon" style={{ background: '#EEF2FD' }}>👤</span>
          Admin Hesabı
        </h3>
        <p className="settings-section-sub">Yönetici hesap bilgileriniz</p>
        <div className="settings-card">
          <div className="settings-row">
            <span className="settings-label">E-posta</span>
            <span className="settings-value">{session?.user?.email || '-'}</span>
          </div>
          <button className="btn-admin btn-admin-outline" style={{ marginTop: 12 }} onClick={signOut}>
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* Platform Info */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <span className="settings-section-icon" style={{ background: '#FFF8E6' }}>🛡️</span>
          Platform Bilgileri
        </h3>
        <p className="settings-section-sub">Genel platform verileri</p>
        <div className="settings-card">
          <div className="platform-stats-grid">
            <div className="platform-stat">
              <span className="platform-stat-value">{stats.users.toLocaleString('tr-TR')}</span>
              <span className="platform-stat-label">Toplam Kullanıcı</span>
            </div>
            <div className="platform-stat">
              <span className="platform-stat-value">{stats.notes.toLocaleString('tr-TR')}</span>
              <span className="platform-stat-label">Toplam Not</span>
            </div>
            <div className="platform-stat">
              <span className="platform-stat-value">{stats.matches.toLocaleString('tr-TR')}</span>
              <span className="platform-stat-label">Toplam Eşleşme</span>
            </div>
            <div className="platform-stat">
              <span className="platform-stat-value" style={{ color: maintEnabled ? '#E65100' : '#2E7D32' }}>
                {maintEnabled ? 'Bakım Modu' : 'Aktif'}
              </span>
              <span className="platform-stat-label">Platform Durumu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Support Email */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <span className="settings-section-icon" style={{ background: '#E3F2FD' }}>✉️</span>
          Destek E-postası
        </h3>
        <p className="settings-section-sub">İletişim bilgileri</p>
        <div className="settings-card">
          <div className="settings-row">
            <span className="settings-label">E-posta Adresi</span>
            <span className="settings-value" style={{ fontWeight: 700 }}>noteswapp@gmail.com</span>
          </div>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="settings-section">
        <h3 className="settings-section-title">
          <span className="settings-section-icon" style={{ background: '#FFF8E6' }}>🔒</span>
          Bakım Modu
        </h3>
        <p className="settings-section-sub">Platform bakımı için geçici kapatma</p>
        <div className="settings-card">
          <div className="maint-toggle-row">
            <div>
              <p style={{ fontWeight: 600, color: '#1A2340', fontSize: '0.9rem' }}>Bakım Modunu Etkinleştir</p>
              <p style={{ fontSize: '0.8rem', color: '#7A8BAD', marginTop: 2 }}>Aktif olduğunda kullanıcılar bakım sayfasını görür.</p>
            </div>
            <div
              className={`toggle-switch${maintEnabled ? ' on' : ''}`}
              onClick={() => setMaintEnabled(prev => !prev)}
            >
              <div className="toggle-knob" />
            </div>
          </div>

          <div className="afield" style={{ marginTop: 16 }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#3D4F7C', marginBottom: 6, display: 'block' }}>Bakım Mesajı</label>
            <textarea
              style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #E0E6F7', borderRadius: 10, fontSize: '0.875rem', fontFamily: 'inherit', resize: 'vertical', minHeight: 80, outline: 'none', color: '#1A2340' }}
              placeholder="Kullanıcılara gösterilecek bakım mesajı..."
              value={maintMsg}
              onChange={e => setMaintMsg(e.target.value)}
            />
          </div>

          {lastUpdated && (
            <p style={{ fontSize: '0.75rem', color: '#7A8BAD', marginTop: 8 }}>
              Son güncelleme: {fmtDate(lastUpdated)}{updatedBy ? ` — ${updatedBy}` : ''}
            </p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button className="btn-admin btn-admin-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
            {saved && <span style={{ fontSize: '0.82rem', color: '#2E7D32', fontWeight: 600 }}>✓ Kaydedildi</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
